import { useCallback, useMemo, useRef } from 'react';
import useStateWithRef from 'hooks/useStateWithRef';
import useDisplayedAttributes from './useDisplayedAttributes';

export default function useTrackingStateContextValue() {
  const [isViewportTracked, setIsViewportTracked, isViewportTrackedRef] =
    useStateWithRef(false);

  const loadTrackedRef = useRef(false);
  const engagementTrackedRef = useRef(false);
  const playedUnitsRef = useRef<string[]>([]);

  const { displayedUnitAttributes, registerDisplayedAttribute } =
    useDisplayedAttributes();

  const getDisplayedUnitAttributes = useCallback(
    () => displayedUnitAttributes.current,
    [displayedUnitAttributes],
  );

  const getIsEngagementTracked = useCallback(
    () => engagementTrackedRef.current,
    [],
  );

  const getIsUnitPlayed = useCallback(
    (unitId: string) => playedUnitsRef.current.includes(unitId),
    [],
  );

  const getIsViewportTracked = useCallback(
    () => isViewportTrackedRef.current,
    [isViewportTrackedRef],
  );

  const getIsWidgetEngagementTracked = useCallback(
    () => engagementTrackedRef.current,
    [],
  );

  const getIsWidgetLoadTracked = useCallback(() => loadTrackedRef.current, []);

  const trackEngagement = useCallback(() => {
    engagementTrackedRef.current = true;
  }, []);

  const trackWidgetEngagement = useCallback(() => {
    engagementTrackedRef.current = true;
  }, []);

  const trackWidgetLoad = useCallback(() => {
    loadTrackedRef.current = true;
  }, []);

  const trackWidgetInViewport = useCallback(() => {
    setIsViewportTracked(true);
  }, [setIsViewportTracked]);

  const trackUnitPlayed = useCallback((unitId: string) => {
    playedUnitsRef.current.push(unitId);
  }, []);

  return useMemo(
    () => ({
      getDisplayedUnitAttributes,
      getIsEngagementTracked,
      getIsUnitPlayed,
      getIsWidgetEngagementTracked,
      getIsWidgetLoadTracked,
      getIsViewportTracked,
      isViewportTracked,
      registerDisplayedAttribute,
      trackEngagement,
      trackUnitPlayed,
      trackWidgetEngagement,
      trackWidgetInViewport,
      trackWidgetLoad,
    }),
    [
      getDisplayedUnitAttributes,
      getIsEngagementTracked,
      getIsUnitPlayed,
      getIsViewportTracked,
      getIsWidgetEngagementTracked,
      getIsWidgetLoadTracked,
      isViewportTracked,
      registerDisplayedAttribute,
      trackEngagement,
      trackUnitPlayed,
      trackWidgetEngagement,
      trackWidgetInViewport,
      trackWidgetLoad,
    ],
  );
}
